import { ArrowRight, MoreHorizontal, Plus, PlugZap, Power, PowerOff } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { TriggerFormDialog } from "@/features/trigger-form";
import { TriggerSecretDialog, type ShownTriggerSecret } from "@/features/trigger-secret-dialog";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCampaigns, useTenants, useTriggers, useUpdateTrigger } from "@/lib/queries";
import type { Trigger } from "@/lib/types";
import { actionTerm } from "@/lib/triggers";
import { relativeTime } from "@/lib/utils";

/**
 * Triggers (ADR-021): the URLs other systems call to act on the tenant's leads. A zap sending new
 * leads is the case the flow is laid out for: make an "Add to a dial list" trigger, put its URL and
 * secret in the zap, and send a test, which shows up on the trigger's page with what it made.
 *
 * Admins only, as the routes are. There is no delete: a trigger is turned off, so what it received
 * stays readable. The secret is shown once, when the trigger is made, and after that only rotated.
 */
export function TriggersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const { data, isPending, error, refetch } = useTriggers();
  const campaigns = useCampaigns();
  const { data: tenants } = useTenants({ enabled: isSuperadmin });
  const update = useUpdateTrigger();
  const [formOpen, setFormOpen] = React.useState(false);
  const [secret, setSecret] = React.useState<ShownTriggerSecret>();
  const campaignNames = new Map((campaigns.data ?? []).map((c) => [c.id, c.name]));

  const setOn = async (trigger: Trigger, enabled: boolean) => {
    try {
      await update.mutateAsync({ id: trigger.id, enabled });
      toast.success(enabled ? "Trigger on" : "Trigger off", trigger.name);
    } catch (e) {
      toast.error(
        enabled ? "Could not turn it on" : "Could not turn it off",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  const newButton = (
    <Button variant="default" onClick={() => setFormOpen(true)}>
      <Plus />
      New trigger
    </Button>
  );

  return (
    <Page>
      <PageHeader
        title="Triggers"
        description="Let another system act on your leads: a zap, a CRM workflow, anything that can send a POST. Each trigger has a URL and a secret of its own."
        action={newButton}
      />

      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load the triggers"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={PlugZap}
            title="No triggers yet"
            description="For a zap that sends new leads, add a trigger that adds them to a campaign's dial list, then put its URL and secret in the zap."
            action={newButton}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trigger</TableHead>
                <TableHead>What it does</TableHead>
                <TableHead>Campaign</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                <TableHead>Last request</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((trigger) => (
                <TableRow key={trigger.id} className="[&>td]:h-12">
                  <TableCell>
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <Link
                        to={`/triggers/${trigger.id}`}
                        className="font-medium text-text underline-offset-4 hover:underline"
                      >
                        {trigger.name}
                      </Link>
                      {!trigger.enabled && (
                        <Badge tone="warning" size="sm">
                          Off
                        </Badge>
                      )}
                    </span>
                    {trigger.description && (
                      <p className="max-w-md truncate text-xs text-text-muted">{trigger.description}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-text">{actionTerm(trigger.action).label}</TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {trigger.campaign_id
                      ? (campaignNames.get(trigger.campaign_id) ?? "A campaign")
                      : trigger.action === "capture"
                        ? "—"
                        : "Every campaign"}
                  </TableCell>
                  {isSuperadmin && (
                    <TableCell className="text-xs text-text-muted">
                      {tenants?.find((t) => t.id === trigger.tenant_id)?.name ?? "—"}
                    </TableCell>
                  )}
                  <TableCell className="text-xs text-text-muted">
                    {trigger.last_received_at ? relativeTime(trigger.last_received_at) : "Never"}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${trigger.name}`}>
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onSelect={() => navigate(`/triggers/${trigger.id}`)}>
                          <ArrowRight />
                          Open
                        </DropdownMenuItem>
                        {trigger.enabled ? (
                          <DropdownMenuItem onSelect={() => void setOn(trigger, false)}>
                            <PowerOff />
                            Turn off
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onSelect={() => void setOn(trigger, true)}>
                            <Power />
                            Turn on
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <TriggerFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onCreated={(created) =>
          setSecret({
            triggerId: created.id,
            name: created.name,
            path: created.path,
            header: created.secret_header,
            secret: created.secret,
            rotated: false,
          })
        }
      />

      {/* After a create, Done goes on to the new trigger's page: how to call it, and what arrives. */}
      <TriggerSecretDialog
        secret={secret}
        onClose={() => {
          const shown = secret;
          setSecret(undefined);
          if (shown && !shown.rotated) navigate(`/triggers/${shown.triggerId}`);
        }}
      />
    </Page>
  );
}
